<script setup>
// ChatListHeader.vue
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  showDebugSql: { type: Boolean, default: false },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const { uiSettings, updateUISettings } = useUISettings();
const store = useStore();

// Debug SQL
const isDebugModalOpen = ref(false);
const copied = ref(false);

const debugSql = computed(() => store.state.conversations?.debugSql || '');

const openDebugModal = () => {
  isDebugModalOpen.value = true;
};

const closeDebugModal = () => {
  isDebugModalOpen.value = false;
};

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(debugSql.value);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (err) {
    console.error('Erro ao copiar:', err);
  }
};

const formatSql = (sqlText) => {
  if (!sqlText) return '';
  return sqlText
    .replace(/\bSELECT\b/gi, '\nSELECT')
    .replace(/\bFROM\b/gi, '\nFROM')
    .replace(/\bWHERE\b/gi, '\nWHERE')
    .replace(/\bAND\b/gi, '\n  AND')
    .replace(/\bOR\b/gi, '\n  OR')
    .replace(/\bEXISTS\b/gi, '\n    EXISTS')
    .trim();
};

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
// No teto do servidor (ConversationCounts) o número é "pelo menos isso".
const countCapped = computed(() => {
  const cap = props.conversationStats?.countCap;
  return Boolean(cap) && allCount.value >= cap;
});
const formattedAllCount = computed(
  () => `${formatNumber(allCount.value)}${countCapped.value ? '+' : ''}`
);

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 px-3 h-12"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center justify-center min-w-0">
      <h1
        class="text-base font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
    </div>
    <div class="flex items-center gap-1">
      <!-- Debug SQL Button -->
      <NextButton
        v-if="showDebugSql && hasAppliedFiltersOrActiveFolders"
        v-tooltip.top-end="'Ver SQL'"
        icon="i-lucide-code"
        slate
        xs
        faded
        @click="openDebugModal"
      />
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-else class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <SwitchLayout
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle="toggleConversationLayout"
      />
    </div>
  </div>

  <!-- Debug SQL Modal -->
  <Teleport to="body">
    <div
      v-if="isDebugModalOpen"
      class="fixed inset-0 z-[200] flex items-center justify-center p-4"
    >
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-sm"
        @click="closeDebugModal"
      />
      <div class="relative z-10 w-full max-w-4xl bg-n-solid-2 border border-n-weak rounded-xl shadow-2xl overflow-hidden">
        <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-solid-3">
          <h3 class="text-sm font-medium text-n-slate-12">
            SQL Gerado
          </h3>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors"
              :class="copied 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4 hover:text-n-slate-12'"
              @click="copyToClipboard"
            >
              {{ copied ? 'Copiado!' : 'Copiar' }}
            </button>
            <button
              type="button"
              class="p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-3 rounded transition-colors"
              @click="closeDebugModal"
            >
              ✕
            </button>
          </div>
        </div>
        <div class="p-4 max-h-[60vh] overflow-auto">
          <pre
            v-if="debugSql"
            class="text-xs font-mono text-n-slate-11 whitespace-pre-wrap break-all bg-n-solid-1 p-4 rounded-lg border border-n-weak"
          >{{ formatSql(debugSql) }}</pre>
          <p v-else class="text-sm text-n-slate-10 text-center py-8">
            Nenhum SQL disponível. Aplique os filtros primeiro.
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>